import React, { useEffect, useState, useRef } from 'react';
import { DataProviderInstance } from '../../../../api/DataProvider';
import styles from './RegistrationData.module.css';
import Loading from '../../../../components/loading/Loading.js';
import InputField from "../../../../components/inputfield/InputField";
import CustomButton from "../../../../components/buttons/CustomButton";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faImage } from '@fortawesome/free-solid-svg-icons';
import ColorPalette from '../../../../colorpalette/ColorPalette';
import AreaTitle from '../../../../components/areaTitle/AreaTitle';
import Button from '@mui/material/Button';
import RemotePaths from '../../../../consts/RemotePaths';
import { Badge, Tooltip, Typography } from "@mui/material";
import { useRoleAccess } from '../../../../hooks/useRoleAccess';
import { USER_ROLES } from '../../../../constants/userRoles';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PanoramaOutlinedIcon from '@mui/icons-material/PanoramaOutlined';

const RegistrationData = () => {
    const dataProvider = DataProviderInstance();
    const { CompanyConfig, GetCEPInfo, UpdateCompany } = dataProvider;
    const { hasRole } = useRoleAccess();
    const [localLoading, setLocalLoading] = useState(false);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    
    // Verificar se o usuário tem permissão para editar (COMPANY_OWNER ou COMPANY_ADMIN)
    const canEdit = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);

    const getInitialLogoPath = () => {
        if (!CompanyConfig || !CompanyConfig.Logo) {
            return CompanyConfig ? RemotePaths.PATH_USER_UPLOADS_COMPANY_LOGO(CompanyConfig.Id) + "default.png" : "";
        }
        return RemotePaths.PATH_USER_UPLOADS_COMPANY_LOGO(CompanyConfig.Id) + CompanyConfig.Logo;
    };

    const getInitialBannerPath = () => {
        if (!CompanyConfig || !CompanyConfig.Banner) {
            return "";
        }
        return RemotePaths.PATH_USER_UPLOADS_COMPANY_BANNER(CompanyConfig.Id) + CompanyConfig.Banner;
    };

    const [editing, setEditing] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedBannerFile, setSelectedBannerFile] = useState(null);
    const [logoPath, setLogoPath] = useState("");
    const [bannerPath, setBannerPath] = useState("");
    const [hasChanges, setHasChanges] = useState(false);
    const [changedFields, setChangedFields] = useState({});
    const [loadingAddress, setLoadingAddress] = useState(false);

    // Form refs
    const inputFieldNameRef = useRef(null);
    const inputFieldCNPJRef = useRef(null);
    const inputFieldCEPRef = useRef(null);
    const inputFieldAddressRef = useRef(null);
    const inputFieldNumberRef = useRef(null);
    const inputFieldComplementRef = useRef(null);
    const inputFieldDistrictRef = useRef(null);
    const inputFieldCityRef = useRef(null);
    const inputFieldStateRef = useRef(null);
    const inputFieldCountryRef = useRef(null);
    const inputFieldPhoneRef = useRef(null);
    const inputFieldEmailRef = useRef(null);
    const inputFieldFileRef = useRef(null);
    const inputFieldBannerFileRef = useRef(null);

    // Form state
    const [name, setName] = useState(CompanyConfig.Name || '');
    const [cnpj, setCnpj] = useState(CompanyConfig.CNPJ || '');
    const [cep, setCep] = useState(CompanyConfig.Cep || '');
    const [address, setAddress] = useState(CompanyConfig.Address || '');
    const [number, setNumber] = useState(CompanyConfig.Number || '');
    const [complement, setComplement] = useState(CompanyConfig.Complement || '');
    const [district, setDistrict] = useState(CompanyConfig.District || '');
    const [city, setCity] = useState(CompanyConfig.City || '');
    const [state, setState] = useState(CompanyConfig.State || '');
    const [country, setCountry] = useState(CompanyConfig.Country || '');
    const [countryCode, setCountryCode] = useState(CompanyConfig.countryCode || '55');
    const [phone, setPhone] = useState(CompanyConfig.Phone || '');
    const [email, setEmail] = useState(CompanyConfig.Email || '');

    // Initialize data when CompanyConfig changes
    useEffect(() => {
        
        if (CompanyConfig) {
            // Update logo path (only if no file is selected)
            if (!selectedFile) {
                const path = getInitialLogoPath();
                setLogoPath(path);
            }

            // Update banner path (only if no file is selected)
            if (!selectedBannerFile) {
                const bPath = getInitialBannerPath();
                setBannerPath(bPath);
            }
            
            // Update form data
            setName(CompanyConfig.Name || '');
            setCnpj(CompanyConfig.CNPJ || '');
            setCep(CompanyConfig.Cep || '');
            setAddress(CompanyConfig.Address || '');
            setNumber(CompanyConfig.Number || '');
            setComplement(CompanyConfig.Complement || '');
            setDistrict(CompanyConfig.District || '');
            setCity(CompanyConfig.City || '');
            setState(CompanyConfig.State || '');
            setCountry(CompanyConfig.Country || '');
            setCountryCode(CompanyConfig.countryCode || '55');
            setPhone(CompanyConfig.Phone || '');
            setEmail(CompanyConfig.Email || '');
            
            // Update input fields if they exist
            if (!initialDataLoaded) {
                setTimeout(() => {
                    updateInputFields();
                    setInitialDataLoaded(true);
                }, 100);
            }
        }
    }, [CompanyConfig, selectedFile, selectedBannerFile]);
    
    // Function to update all input fields with current values
    const updateInputFields = () => {
        if (!CompanyConfig) return;
        
        const fieldsToUpdate = [
            { ref: inputFieldNameRef, value: CompanyConfig.Name },
            { ref: inputFieldCNPJRef, value: CompanyConfig.CNPJ },
            { ref: inputFieldCEPRef, value: CompanyConfig.Cep },
            { ref: inputFieldAddressRef, value: CompanyConfig.Address },
            { ref: inputFieldNumberRef, value: CompanyConfig.Number },
            { ref: inputFieldComplementRef, value: CompanyConfig.Complement },
            { ref: inputFieldDistrictRef, value: CompanyConfig.District },
            { ref: inputFieldCityRef, value: CompanyConfig.City },
            { ref: inputFieldStateRef, value: CompanyConfig.State },
            { ref: inputFieldCountryRef, value: CompanyConfig.Country },
            { ref: inputFieldPhoneRef, value: CompanyConfig.Phone },
            { ref: inputFieldEmailRef, value: CompanyConfig.Email }
        ];
        
        fieldsToUpdate.forEach(({ ref, value }) => {
            if (ref && ref.current && typeof ref.current.SetValue == 'function') {
                ref.current.SetValue(value || '');
            }
        });
    };

    const SectionHeader = ({ icon: Icon, title, description }) => {
        const iconElement = <Icon style={{ fontSize: 24 }} />;
        return (
            <div className={styles.sectionHeader}>
                <AreaTitle
                    Title={title}
                    Icon={iconElement}
                    Description={description}
                />
            </div>
        );
    };

    const ActionButtons = () => (
        <div className={styles.actionButtons}>
            {editing ? (
                <>
                    <CustomButton
                        variant="contained"
                        label="Salvar"
                        style="secondary"
                        onClick={saveClickHandler}
                        startIcon={<SaveOutlinedIcon />}
                    />
                    <CustomButton
                        variant="outlined"
                        label="Cancelar"
                        style="primary"
                        onClick={cancelClickHandler}
                        startIcon={<CloseOutlinedIcon />}
                    />
                </>
            ) : (
                <Tooltip
                    title={!canEdit ? "Apenas proprietários e administradores podem editar os dados cadastrais" : ""}
                    arrow
                >
                    <span>
                        <CustomButton
                            variant="contained"
                            label="Editar"
                            style="secondary"
                            onClick={() => setEditing(true)}
                            startIcon={<EditOutlinedIcon />}
                            disabled={!canEdit}
                        />
                    </span>
                </Tooltip>
            )}
        </div>
    );

    function OnSearchCEPClickHandler() {
        let cep = inputFieldCEPRef.current.GetValue();
        if (cep.trim() != "") {
            GetFullAdressByCEP(cep);
        }
    }

    function GetFullAdressByCEP(value) {
        setLoadingAddress(true);
        GetCEPInfo(value, (result) => {
            inputFieldAddressRef.current.SetValue(result.logradouro);
            inputFieldDistrictRef.current.SetValue(result.bairro);
            inputFieldCityRef.current.SetValue(result.localidade);
            inputFieldStateRef.current.SetValue(result.uf);
            inputFieldCountryRef.current.SetValue("Brasil");

            setAddress(result.logradouro);
            setDistrict(result.bairro);
            setCity(result.localidade);
            setState(result.uf);
            setLoadingAddress(false);
        });
    }

    function onUpdateValue(setter, field, originalValue) {
        return (value) => {
            setter(value);
            const hasChanged = value != originalValue;
            setHasChanges(hasChanged);
            setChangedFields(prev => {
                const newFields = { ...prev };
                if (hasChanged) {
                    newFields[field] = true;
                } else {
                    delete newFields[field];
                }
                return newFields;
            });
        };
    }

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setLogoPath(URL.createObjectURL(file));
            setHasChanges(true);
        }
    };

    const handleBannerFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedBannerFile(file);
            setBannerPath(URL.createObjectURL(file));
            setHasChanges(true);
        }
    };

    const clearFile = () => {
        if (inputFieldFileRef.current) {
            inputFieldFileRef.current.value = '';
        }
        setSelectedFile(null);
    };

    const clearBannerFile = () => {
        if (inputFieldBannerFileRef.current) {
            inputFieldBannerFileRef.current.value = '';
        }
        setSelectedBannerFile(null);
    };

    const cancelClickHandler = () => {
        setName(CompanyConfig.Name);
        setCnpj(CompanyConfig.CNPJ);
        setCep(CompanyConfig.Cep);
        setAddress(CompanyConfig.Address);
        setNumber(CompanyConfig.Number);
        setComplement(CompanyConfig.Complement);
        setDistrict(CompanyConfig.District);
        setCity(CompanyConfig.City);
        setState(CompanyConfig.State);
        setCountry(CompanyConfig.Country);
        setPhone(CompanyConfig.Phone);
        setEmail(CompanyConfig.Email);
        setChangedFields({});
        setHasChanges(false);
        setEditing(false);
        clearFile();
        clearBannerFile();

        // Reset all input fields
        [
            { ref: inputFieldNameRef, value: CompanyConfig.Name },
            { ref: inputFieldCNPJRef, value: CompanyConfig.CNPJ },
            { ref: inputFieldCEPRef, value: CompanyConfig.Cep },
            { ref: inputFieldAddressRef, value: CompanyConfig.Address },
            { ref: inputFieldNumberRef, value: CompanyConfig.Number },
            { ref: inputFieldComplementRef, value: CompanyConfig.Complement },
            { ref: inputFieldDistrictRef, value: CompanyConfig.District },
            { ref: inputFieldCityRef, value: CompanyConfig.City },
            { ref: inputFieldStateRef, value: CompanyConfig.State },
            { ref: inputFieldCountryRef, value: CompanyConfig.Country },
            { ref: inputFieldPhoneRef, value: CompanyConfig.Phone },
            { ref: inputFieldEmailRef, value: CompanyConfig.Email }
        ].forEach(({ ref, value }) => ref.current.SetValue(value));
    };

    const saveClickHandler = () => {
        setEditing(false);
        setLocalLoading(true);
        const formData = {
            logo: selectedFile,
            banner: selectedBannerFile,
            name,
            cnpj,
            cep,
            address,
            number,
            complement,
            district,
            city,
            state,
            country,
            countryCode,
            phone,
            email
        };

        
        UpdateCompany(formData, (result) => {
            
            if (result) {
                setChangedFields({});
                setHasChanges(false);
                clearFile();
                clearBannerFile();
                
                // Force refresh of company data after saving
                if (dataProvider && typeof dataProvider.GetCompany == 'function') {
                    
                    dataProvider.GetCompany((refreshedData) => {
                        
                        setInitialDataLoaded(false); // Reset to trigger field updates
                    });
                }
            }
            setLocalLoading(false);
        });
    };

    // Create the icon for the AreaTitle
    const companyIcon = <BusinessOutlinedIcon style={{ fontSize: 24 }} />;

    return (
        <div className={styles.mainContainer}>
            <div className={styles.titleContainer}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <AreaTitle
                        Title="Dados cadastrais"
                        Icon={companyIcon}
                        Description="Gerencie as informações cadastrais da sua empresa"
                    />
                    {Object.keys(changedFields).length > 0 && (
                        <Tooltip title="Dados alterados" arrow>
                            <Badge badgeContent={Object.keys(changedFields).length} color="error" />
                        </Tooltip>
                    )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                    {localLoading && (
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <Loading size={20} />
                            <Typography variant="caption" style={{ marginLeft: "8px" }}>
                                Salvando dados...
                            </Typography>
                        </div>
                    )}
                    <ActionButtons />
                </div>
            </div>

            {/* Company Information Section */}
            <div className={styles.section}>
                <SectionHeader
                    icon={BusinessOutlinedIcon}
                    title="Informações da Empresa"
                    description="Dados básicos de identificação da sua empresa"
                />
                <div className={styles.companyInfoContainer}>
                    <div className={styles.logoContainer}>
                        <div className={styles.logoBox}>
                            {logoPath && logoPath.includes('default.png') ? (
                                <ImageOutlinedIcon className={styles.logoPlaceholder} />
                            ) : (
                                <img
                                    src={logoPath}
                                    alt="Logo"
                                    className={styles.logo}
                                />
                            )}
                        </div>
                        <div>
                            <Button
                                variant="contained"
                                component="label"
                                disabled={!editing}
                                color="primary"
                                sx={{
                                    backgroundColor: editing ? ColorPalette.blueMain : undefined,
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    padding: '8px 16px'
                                }}
                            >
                                Carregar logo
                                <input
                                    ref={inputFieldFileRef}
                                    hidden
                                    type="file"
                                    accept=".png,.jpg,.jpeg"
                                    onChange={handleFileChange}
                                />
                            </Button>
                            <Typography className={styles.logoLabel}>
                                Formatos aceitos: PNG, JPG, JPEG
                            </Typography>
                        </div>
                    </div>
                    <div className={styles.companyInfoFields}>
                        <div className={styles.formGroup}>
                            <div style={{ marginBottom: '16px' }}>
                                <InputField
                                    Mandatory={false}
                                    Value={name}
                                    Label="Nome da Empresa"
                                    ref={inputFieldNameRef}
                                    OnUpdateValue={onUpdateValue(setName, 'name', CompanyConfig.Name)}
                                    className={changedFields.name ? styles.changedField : ''}
                                    Disabled={!editing}
                                />
                            </div>
                            <div>
                                <InputField
                                    Mandatory={false}
                                    Value={cnpj}
                                    Label="CNPJ"
                                    ref={inputFieldCNPJRef}
                                    Mask="99.999.999/9999-99"
                                    OnUpdateValue={onUpdateValue(setCnpj, 'cnpj', CompanyConfig.CNPJ)}
                                    className={changedFields.cnpj ? styles.changedField : ''}
                                    Disabled={!editing}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Banner Section */}
                <div className={styles.bannerSection}>
                    <div className={styles.bannerLabelRow}>
                        <PanoramaOutlinedIcon style={{ fontSize: 20, color: 'var(--color-text-light)' }} />
                        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                            Banner da empresa
                        </Typography>
                        <Typography variant="caption" style={{ color: 'var(--color-text-light)' }}>
                            Imagem exibida como capa no agendamento público (recomendado: 1200×300px)
                        </Typography>
                    </div>
                    <div className={styles.bannerBox}>
                        {bannerPath ? (
                            <img
                                src={bannerPath}
                                alt="Banner"
                                className={styles.banner}
                            />
                        ) : (
                            <div className={styles.bannerPlaceholder}>
                                <PanoramaOutlinedIcon style={{ fontSize: 48, color: 'var(--color-text-light)' }} />
                                <Typography variant="caption" style={{ color: 'var(--color-text-light)', marginTop: '8px' }}>
                                    Nenhum banner cadastrado
                                </Typography>
                            </div>
                        )}
                    </div>
                    <div style={{ marginTop: '12px' }}>
                        <Button
                            variant="contained"
                            component="label"
                            disabled={!editing}
                            color="primary"
                            sx={{
                                backgroundColor: editing ? ColorPalette.blueMain : undefined,
                                textTransform: 'none',
                                borderRadius: '8px',
                                padding: '8px 16px'
                            }}
                        >
                            Carregar banner
                            <input
                                ref={inputFieldBannerFileRef}
                                hidden
                                type="file"
                                accept=".png,.jpg,.jpeg"
                                onChange={handleBannerFileChange}
                            />
                        </Button>
                        <Typography className={styles.logoLabel}>
                            Formatos aceitos: PNG, JPG, JPEG
                        </Typography>
                    </div>
                </div>
            </div>

            {/* Address Section */}
            <div className={styles.section}>
                <SectionHeader
                    icon={LocationOnOutlinedIcon}
                    title="Endereço"
                    description="Localização física da sua empresa"
                />
                <div className={styles.formGroup}>
                    <div className={styles.formRow}>
                        <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                            <InputField
                                Mandatory={false}
                                Value={cep}
                                Label="CEP"
                                ref={inputFieldCEPRef}
                                Mask="99999-999"
                                OnUpdateValue={onUpdateValue(setCep, 'cep', CompanyConfig.Cep)}
                                className={changedFields.cep ? styles.changedField : ''}
                                Disabled={!editing}
                            />
                            <CustomButton
                                style="secondary"
                                variant="outlined"
                                icon={<SearchOutlinedIcon style={{ fontSize: '16px' }} />}
                                onClick={OnSearchCEPClickHandler}
                                enabled={editing}
                            />
                        </div>
                        {loadingAddress && (
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <Loading size={20} />
                                <Typography variant="caption" style={{ marginLeft: "8px" }}>
                                    Buscando endereço...
                                </Typography>
                            </div>
                        )}
                    </div>
                    
                    <div className={styles.formRow}>
                        <InputField
                            Mandatory={false}
                            Value={address}
                            Label="Rua"
                            ref={inputFieldAddressRef}
                            OnUpdateValue={onUpdateValue(setAddress, 'address', CompanyConfig.Address)}
                            className={changedFields.address ? styles.changedField : ''}
                            Disabled={!editing}
                        />
                        <InputField
                            Mandatory={false}
                            Value={number}
                            Label="Número"
                            ref={inputFieldNumberRef}
                            OnUpdateValue={onUpdateValue(setNumber, 'number', CompanyConfig.Number)}
                            className={changedFields.number ? styles.changedField : ''}
                            Disabled={!editing}
                        />
                        <InputField
                            Mandatory={false}
                            Value={complement}
                            Label="Complemento"
                            ref={inputFieldComplementRef}
                            OnUpdateValue={onUpdateValue(setComplement, 'complement', CompanyConfig.Complement)}
                            className={changedFields.complement ? styles.changedField : ''}
                            Disabled={!editing}
                        />
                    </div>
                    
                    <div className={styles.formRow}>
                        <InputField
                            Mandatory={false}
                            Value={district}
                            Label="Bairro"
                            ref={inputFieldDistrictRef}
                            OnUpdateValue={onUpdateValue(setDistrict, 'district', CompanyConfig.District)}
                            className={changedFields.district ? styles.changedField : ''}
                            Disabled={!editing}
                        />
                        <InputField
                            Mandatory={false}
                            Value={city}
                            Label="Cidade"
                            ref={inputFieldCityRef}
                            OnUpdateValue={onUpdateValue(setCity, 'city', CompanyConfig.City)}
                            className={changedFields.city ? styles.changedField : ''}
                            Disabled={!editing}
                        />
                        <InputField
                            Mandatory={false}
                            Value={state}
                            Label="Estado"
                            ref={inputFieldStateRef}
                            OnUpdateValue={onUpdateValue(setState, 'state', CompanyConfig.State)}
                            className={changedFields.state ? styles.changedField : ''}
                            Disabled={!editing}
                        />
                        <InputField
                            Mandatory={false}
                            Value={country}
                            Label="País"
                            ref={inputFieldCountryRef}
                            OnUpdateValue={onUpdateValue(setCountry, 'country', CompanyConfig.Country)}
                            className={changedFields.country ? styles.changedField : ''}
                            Disabled={!editing}
                        />
                    </div>
                </div>
            </div>

            {/* Contact Section */}
            <div className={styles.section}>
                <SectionHeader
                    icon={ContactPhoneOutlinedIcon}
                    title="Contatos"
                    description="Informações de contato da sua empresa"
                />
                <div className={styles.formGroup}>
                    <div className={styles.formRow}>
                        <div style={{ maxWidth: '100px' }}>
                            <InputField
                                Mandatory={false}
                                Value={countryCode}
                                Label="DDI"
                                Mask="999"
                                OnUpdateValue={onUpdateValue(setCountryCode, 'countryCode', CompanyConfig.countryCode || '55')}
                                className={changedFields.countryCode ? styles.changedField : ''}
                                Disabled={!editing}
                            />
                        </div>
                        <InputField
                            Mandatory={false}
                            Value={phone}
                            Label="Telefone"
                            ref={inputFieldPhoneRef}
                            Mask="(99) 99999-9999"
                            OnUpdateValue={onUpdateValue(setPhone, 'phone', CompanyConfig.Phone)}
                            className={changedFields.phone ? styles.changedField : ''}
                            Disabled={!editing}
                        />
                        <InputField
                            Mandatory={false}
                            Value={email}
                            Label="Email"
                            ref={inputFieldEmailRef}
                            OnUpdateValue={onUpdateValue(setEmail, 'email', CompanyConfig.Email)}
                            className={changedFields.email ? styles.changedField : ''}
                            Disabled={!editing}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegistrationData;
